'use client';

import {useAuthorization} from '@/matic/hooks/useAuthorization';
import {useNavigate, Outlet} from 'react-router-dom';
import {useEffect} from 'react';
import LoadingSpinner from '@/matic/components/ui/LoadingSpinner';
import {useAppContext} from '@/matic/providers/AppProvider';

/**
 * Verify this solution against: KNOWN ISSUE - Access control inconsistency
 * Training Program Layout - Role-based guard for training program routes
 *
 * This layout component acts as a role-based guard for training program routes.
 * It checks if the current user has 'training.access_programs' permission.
 * Only Admin and Quality roles can access training programs management.
 * If not authorized, redirects to the standardized /unauthorized page.
 *
 * Layer 1 (Authentication): Handled by parent ProtectedLayout
 * Layer 2 (Role-Based Authorization): This component checks training.access_programs
 */
export function TrainingProgramLayout() {
  const {user} = useAppContext();
  const {can} = useAuthorization();
  const navigate = useNavigate();

  const hasAccess = can('training.access_programs');

  useEffect(() => {
    if (user && !hasAccess) {
      navigate('/unauthorized');
    }
  }, [user, hasAccess, navigate]);

  // Only show the loading M on a true cold boot (no user yet). `user` is the
  // *stable* user from AppContext, so it stays defined during silent OIDC
  // token refreshes — preventing M flashes on tab switches into /training/*.
  if (!user) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // UX-only authorization check - backend enforces actual authorization
  if (!hasAccess) {
    return null; // Redirect will happen via useEffect
  }

  return <Outlet />;
}
